import React, { useCallback, useRef } from 'react';
import { Box, CircularProgress, Grid } from '@material-ui/core';
import { useIntersectionObserver } from '../../../utils';
import { VenueShifts } from './types';
import CashFlowListItem from './CashFlowListItem';

const LoadMoreElement: React.FC<{ isLoading: boolean; ref: React.Ref<HTMLDivElement> }> = React.forwardRef(({
    isLoading
}, ref) => (
    <div ref={ref}>
        <Box display='flex' flexGrow={1} justifyContent='center'>
            {isLoading ? <CircularProgress /> : null}
        </Box>
    </div>
));

export interface CashFlowListViewProps {
    venueShifts: VenueShifts;
    loadMore(): Promise<void>;
    hasMore: boolean;
}

const CashFlowListView: React.FC<CashFlowListViewProps> = ({ venueShifts, loadMore, hasMore }) => {
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const load = useCallback(async () => {
        setIsLoading(true);
        await loadMore();
        setIsLoading(false);
    }, [loadMore]);

    const loaderRef = useRef<HTMLDivElement>(null);

    const onLoadMore: IntersectionObserverCallback = useCallback(async entries => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !isLoading) {
            await load();
        }
    }, [load, isLoading, hasMore]);

    useIntersectionObserver(loaderRef, onLoadMore);

    return (
        <Grid container spacing={3}>
            {venueShifts.map(venueShift => <CashFlowListItem venueShift={venueShift} key={venueShift.id} />)}
            <Grid item xs={12}>
                {hasMore ? <LoadMoreElement ref={loaderRef} isLoading={isLoading} /> : null}
            </Grid>
        </Grid>
    );
};

export default CashFlowListView;
